import React, { useEffect, useState, useCallback } from 'react';
import { useOutletContext, useNavigate, useParams, Navigate } from 'react-router-dom';
import { portalApi, getPreviewId, PORTAL_TOKEN_KEY } from './portalApi';
import { PersonaPreviewCard } from '../components/PersonaPreviewCard';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tooltip as UITooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '../components/ui/tooltip';
import { Badge } from '../components/ui/badge';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Loader2, ArrowLeft, Clock, Info, FileText, Sparkles } from 'lucide-react';
import { SalesProjectionView, PagoInfo } from '../components/SalesProjectionView';
import { LogisticsReport } from '../components/logistics/LogisticsReport';
import { Reports } from '../pages/Reports';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PERIODS = [
  { value: 'hoje', label: 'Hoje' }, { value: 'ontem', label: 'Ontem' },
  { value: 'ultimos_7_dias', label: 'Últimos 7 dias' }, { value: 'ultimos_30_dias', label: 'Últimos 30 dias' },
  { value: 'ultimos_6_meses', label: 'Últimos 6 meses' }, { value: 'semana_atual', label: 'Semana atual' },
  { value: 'mes_atual', label: 'Mês atual' },
];
const brl = (v) => (v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const pctStr = (p) => (p === null || p === undefined ? '—' : `${p > 0 ? '+' : ''}${p.toFixed(1).replace('.', ',')}%`);
const pctClass = (p) => (p === null || p === undefined ? 'text-muted-foreground' : p >= 0 ? 'text-green-600' : 'text-red-600');

const PagoLine = ({ pago, pct, variationPago, showInfo }) => (
  <p className="text-xs text-emerald-700 mt-0.5 flex items-center gap-1 flex-wrap" data-testid="pago-line">
    <span>Pago: {brl(pago)}{pct !== null && pct !== undefined ? ` · ${pct.toFixed(1).replace('.', ',')}% do vendido` : ''}</span>
    {variationPago !== null && variationPago !== undefined && (
      <span className={`font-medium ${pctClass(variationPago)}`}>({pctStr(variationPago)})</span>
    )}
    {showInfo && <PagoInfo />}
  </p>
);

const EmptyState = ({ icon: Icon = Clock, title, text }) => (
  <Card><CardContent className="py-16 flex flex-col items-center text-center gap-3" data-testid="portal-native-empty">
    <Icon className="h-8 w-8 text-slate-400" />
    <p className="text-lg font-medium text-slate-700">{title}</p>
    {text && <p className="text-sm text-muted-foreground max-w-sm">{text}</p>}
  </CardContent></Card>
);

const PageTitle = ({ children }) => <h1 className="text-2xl font-bold text-slate-800 mb-5">{children}</h1>;

// ------- Index: redirect to first enabled item or empty state -------
export const PortalIndex = () => {
  const { me } = useOutletContext();
  const items = me?.menu_items || [];
  if (items.length === 0) {
    return <EmptyState icon={Sparkles} title="Seu portal está sendo preparado pela equipe Proex Digital." />;
  }
  const first = items[0];
  if (first.type === 'link_externo') {
    // External-only first item: show empty state (links open in new tab from the menu)
    const nativeFirst = items.find((i) => i.type === 'nativo');
    if (nativeFirst) return <Navigate to={`/portal/${nativeFirst.key}`} replace />;
    return <EmptyState icon={Sparkles} title="Seu portal está sendo preparado pela equipe Proex Digital." />;
  }
  return <Navigate to={`/portal/${first.key}`} replace />;
};

// ------- Native page dispatcher (guards disabled items) -------
export const PortalNativePage = () => {
  const { key } = useParams();
  const { me } = useOutletContext();
  const items = me?.menu_items || [];
  const enabled = items.some((i) => i.type === 'nativo' && i.key === key);
  if (!enabled) return <Navigate to="/portal" replace />;
  if (key === 'vendas') return <PortalVendas />;
  if (key === 'metricas') return <PortalMetricas />;
  if (key === 'analises') return <PortalAnalisesList />;
  if (key === 'personas') return <PortalPersonas />;
  if (key === 'logistica') return <PortalLogistica />;
  return <Navigate to="/portal" replace />;
};

const portalLogisticsApi = {
  get: (p, cfg) => portalApi.get(`/api/portal/logistica${p}`, cfg),
  saveLimit: (v) => portalApi.put('/api/portal/logistica/frete-gratis-limite', { frete_gratis_limite: v }),
};
const PortalLogistica = () => <LogisticsReport api={portalLogisticsApi} isPortal />;

// ------- VENDAS -------
const PortalVendas = () => {
  const [periodo, setPeriodo] = useState('hoje');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await portalApi.get('/api/portal/vendas', { params: { periodo } });
      setData(data);
    } catch (e) { setData(null); }
    finally { setLoading(false); }
  }, [periodo]);
  useEffect(() => { load(); }, [load]);

  const updatedTime = data?.updated_at ? new Date(data.updated_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : null;

  return (
    <div data-testid="portal-vendas">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-5">
        <PageTitle>Resumo das vendas</PageTitle>
        <Select value={periodo} onValueChange={setPeriodo}>
          <SelectTrigger className="w-[180px]" data-testid="vendas-period-select"><SelectValue /></SelectTrigger>
          <SelectContent>{PERIODS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div>
      ) : !data || !data.wake_active ? (
        <EmptyState icon={Info} title="Os dados de vendas ainda não estão disponíveis." />
      ) : (
        <div className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-3">
            <Card data-testid="vendas-card-period">
              <CardContent className="py-4">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground uppercase tracking-wide">
                  {data.period.card_title}
                  <TooltipProvider><UITooltip><TooltipTrigger asChild><Info className="h-3.5 w-3.5 cursor-help" /></TooltipTrigger>
                    <TooltipContent><p>Bruto: {brl(data.totals.period.bruto)}</p><p>Pago: {brl(data.totals.period.pago)}</p></TooltipContent>
                  </UITooltip></TooltipProvider>
                </div>
                <p className="text-2xl font-bold mt-1">{brl(data.totals.period.bruto)}</p>
                <PagoLine pago={data.totals.period.pago} pct={data.totals.period.pct_pago} showInfo />
                <p className="text-xs text-muted-foreground mt-0.5">{data.period.range_label}</p>
              </CardContent>
            </Card>
            <Card data-testid="vendas-card-comp1">
              <CardContent className="py-4">
                <p className="text-xs text-muted-foreground uppercase tracking-wide truncate">{data.comparison1.label}</p>
                <p className="text-2xl font-bold mt-1">{brl(data.totals.comparison1.bruto)}</p>
                <p className={`text-xs mt-0.5 font-medium ${pctClass(data.totals.variation1.bruto)}`}>{pctStr(data.totals.variation1.bruto)}</p>
                <PagoLine pago={data.totals.comparison1.pago} pct={data.totals.comparison1.pct_pago} variationPago={data.totals.variation1.pago} />
              </CardContent>
            </Card>
            <Card data-testid="vendas-card-comp2">
              <CardContent className="py-4">
                {data.comparison2 ? (
                  <>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide truncate">{data.comparison2.label}</p>
                    <p className="text-2xl font-bold mt-1">{brl(data.totals.comparison2.bruto)}</p>
                    <p className={`text-xs mt-0.5 font-medium ${pctClass(data.totals.variation2?.bruto)}`}>{pctStr(data.totals.variation2?.bruto)}</p>
                    <PagoLine pago={data.totals.comparison2.pago} pct={data.totals.comparison2.pct_pago} variationPago={data.totals.variation2?.pago} />
                  </>
                ) : (
                  <>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide flex items-center gap-1">Pedidos / Ticket médio <PagoInfo /></p>
                    <p className="text-2xl font-bold mt-1">{data.totals.period.pedidos} <span className="text-sm font-normal text-muted-foreground">· pagos: {data.totals.period.pedidos_pagos}</span></p>
                    <p className="text-xs text-muted-foreground mt-0.5">Ticket vendido: {brl(data.totals.period.ticket_medio)} · pago: {brl(data.totals.period.ticket_medio_pago)}</p>
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader className="flex-row items-center justify-between">
              <CardTitle className="text-base">Vendas no período</CardTitle>
              {updatedTime && <span className="text-xs text-muted-foreground">atualizado às {updatedTime}</span>}
            </CardHeader>
            <CardContent>
              <div style={{ width: '100%', height: 300 }} data-testid="vendas-chart">
                <ResponsiveContainer>
                  <LineChart data={data.totals.series} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                    <XAxis dataKey="data" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `R$${(v / 1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v) => brl(v)} />
                    <Legend />
                    <Line type="monotone" dataKey="bruto" name="Bruto" stroke="#6366f1" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="pago" name="Pago" stroke="#10b981" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <ProjecaoSection />
        </div>
      )}
    </div>
  );
};

// ------- PROJEÇÃO (não depende do filtro de período) -------
const ProjecaoSection = () => {
  const [p, setP] = useState(undefined);
  useEffect(() => { portalApi.get('/api/portal/vendas/projecao').then((r) => setP(r.data)).catch(() => setP(null)); }, []);
  return <SalesProjectionView data={p} />;
};

// ------- METRICAS (página Relatórios completa, modo portal) -------
const PortalMetricas = () => {
  const { me } = useOutletContext();
  const project = me?.project || {};
  return (
    <div data-testid="portal-metricas">
      <Reports
        mode="portal"
        projectId={project.id}
        projectName={project.name}
        apiClient={portalApi}
        buildUrl={(suffix) => `${API_URL}/api/portal/relatorios/${suffix}`}
      />
    </div>
  );
};

// ------- ANALISES -------
const PortalAnalisesList = () => {
  const [items, setItems] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { portalApi.get('/api/portal/analises').then((r) => setItems(r.data)).catch(() => setItems([])); }, []);

  return (
    <div data-testid="portal-analises">
      <PageTitle>Análises técnicas</PageTitle>
      {items === null ? (
        <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div>
      ) : items.length === 0 ? (
        <EmptyState icon={FileText} title="Nenhuma análise publicada ainda." text="Assim que a equipe publicar uma análise, ela aparecerá aqui." />
      ) : (
        <div className="space-y-3">
          {items.map((a) => (
            <Card key={a.id} className="cursor-pointer hover:border-indigo-300 transition-colors" onClick={() => navigate(`/portal/analises/${a.id}`)} data-testid={`portal-analise-${a.id}`}>
              <CardContent className="py-4 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-medium truncate">{a.title}</p>
                  <p className="text-xs text-muted-foreground">{a.author_name} · {a.published_at ? new Date(a.published_at).toLocaleDateString('pt-BR') : ''}</p>
                </div>
                <FileText className="h-5 w-5 text-slate-400 shrink-0" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export const PortalAnaliseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [a, setA] = useState(undefined);
  useEffect(() => { portalApi.get(`/api/portal/analises/${id}`).then((r) => setA(r.data)).catch(() => setA(null)); }, [id]);

  if (a === undefined) return <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div>;
  if (a === null) return <EmptyState icon={FileText} title="Análise não encontrada." />;

  // Append auth token to image URLs so <img> requests authenticate (no bearer header possible)
  const preview = getPreviewId();
  const authQ = preview
    ? `token=${encodeURIComponent(localStorage.getItem('token') || '')}&preview_project_id=${encodeURIComponent(preview)}`
    : `token=${encodeURIComponent(localStorage.getItem(PORTAL_TOKEN_KEY) || '')}`;
  const content = (a.content || '').replace(
    /(src=")(\/api\/portal\/arquivos\/[^"]+)(")/g,
    (m, p1, url, p3) => `${p1}${url}${url.includes('?') ? '&' : '?'}${authQ}${p3}`
  );

  return (
    <div className="max-w-4xl mx-auto" data-testid="portal-analise-detail">
      <Button variant="ghost" size="sm" onClick={() => navigate('/portal/analises')} className="mb-4" data-testid="portal-analise-back"><ArrowLeft className="h-4 w-4 mr-1" />Voltar</Button>
      <h1 className="text-2xl font-bold text-slate-800">{a.title}</h1>
      <p className="text-sm text-muted-foreground mb-2">{a.author_name} · {a.published_at ? new Date(a.published_at).toLocaleDateString('pt-BR') : ''}</p>
      {a.tags?.length > 0 && <div className="flex flex-wrap gap-1 mb-5">{a.tags.map((t) => <Badge key={t} variant="secondary" className="text-xs">{t}</Badge>)}</div>}
      <Card><CardContent className="py-6">
        <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: content }} />
      </CardContent></Card>
    </div>
  );
};

// ------- PERSONAS -------
const PortalPersonas = () => {
  const [items, setItems] = useState(null);
  const [selected, setSelected] = useState(null);
  useEffect(() => { portalApi.get('/api/portal/personas').then((r) => setItems(r.data)).catch(() => setItems([])); }, []);

  if (items === null) return <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div>;

  return (
    <div data-testid="portal-personas">
      <PageTitle>Personas</PageTitle>
      {items.length === 0 ? (
        <EmptyState icon={Sparkles} title="Nenhuma persona disponível ainda." />
      ) : selected ? (
        <div>
          <Button variant="ghost" size="sm" onClick={() => setSelected(null)} className="mb-4" data-testid="portal-persona-back"><ArrowLeft className="h-4 w-4 mr-1" />Voltar</Button>
          <div className="max-w-2xl mx-auto"><PersonaPreviewCard persona={selected} expanded /></div>
        </div>
      ) : (
        <div className="grid gap-5 md:grid-cols-2">
          {items.map((p) => (
            <div key={p.id} data-testid={`portal-persona-${p.id}`}>
              <PersonaPreviewCard persona={p} onClick={() => setSelected(p)} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
